/* eslint-disable import/prefer-default-export */
import {
  createInfiniteQuery,
  type CreateInfiniteQueryResult,
  type InfiniteQueryObserverResult
} from '@tanstack/svelte-query';
import type { FeedResponse, FPost, PostDocument , WithCreatorData } from 'garden-common';
import type { Writable } from 'svelte/store';
import { writable, get } from 'svelte/store';
import { cloneDeep } from 'lodash-es';

import type { PostFiltersSchemaWithDynamic, SortOption } from './feed.util';
import { api } from './index'
import { me } from '$src/stores/user.store';

export class FeedContentProvider {
  limit = 2;

  forYouQuery: CreateInfiniteQueryResult;

  forYouPage = 0;

  forYouUpdated = false;

  exploreQuery: CreateInfiniteQueryResult;

  explorePage = 1;

  filters: PostFiltersSchemaWithDynamic;

  sort: SortOption;

  forYouPosts: Writable<(PostDocument & WithCreatorData)[]>;

  explorePosts: Writable<(PostDocument & WithCreatorData)[]>;

  constructor(filters: PostFiltersSchemaWithDynamic, sort: SortOption) {
    this.setFilters(filters);
    this.setSort(sort);

    this.forYouPosts = writable([]);
    this.explorePosts = writable([]);

    this.forYouQuery = createInfiniteQuery({
      queryKey: ['forYou'],
      queryFn: async () => {
        if(!get(me)) return;
        this.forYouPage += 1;
        
        const res = (await api.post(
          '/v2/feed/for-you',
          { filters: this.filters },
          { params: { page: this.forYouPage, limit: this.limit, sort: this.sort } }
        ));

        const posts = get(this.forYouPosts);
        const prevLength = posts.length;
        posts.push(...res.data.results);
        const newLength = posts.length;
        this.forYouPosts.set(posts);

        this.forYouUpdated = newLength > prevLength;

        return res.data;
      },
      getNextPageParam: lastPage => {
        if (lastPage.results.length < this.limit) {
          return undefined;
        }

        return this.forYouPage;
      },
      refetchOnWindowFocus: false
    });

    this.exploreQuery = createInfiniteQuery({
      queryKey: ['explore'],
      queryFn: async () => {
        const res = (await api.post(
          '/v2/feed/explore/posts',
          {},
          { params: { page: this.explorePage, limit: this.limit } }
        ));

        const posts = get(this.explorePosts);
        posts.push(...res.data.results);
        this.explorePosts.set(posts);

        this.explorePage += 1;

        return res.data;
      },
      getNextPageParam: lastPage => {
        if (lastPage.results.length < this.limit) {
          return;
        }

        return this.explorePage;
      },
      enabled: false,
      refetchOnWindowFocus: false
    });
  }

  setSort(sort: SortOption) {
    this.sort = sort;
  }

  setFilters(filters: PostFiltersSchemaWithDynamic) {
    this.filters = cloneDeep(filters);
  }

  async fetchForYouPage() {
    return get(this.forYouQuery).fetchNextPage();
  }

  async fetchExplorePage() {
    return get(this.exploreQuery).fetchNextPage();
  }

  async fetchForYouOrExplorePage(): Promise<void> {
    const forYouQuery = get(this.forYouQuery);
    const exploreQuery = get(this.exploreQuery);

    if (!forYouQuery || !exploreQuery) return;

    if (forYouQuery.hasNextPage || get(this.forYouPosts).length === 0 || this.forYouUpdated) {
      const res = (await this.fetchForYouPage()) as {
        data: { pages: { nextPage: number | null; posts: FPost<string>[] }[] };
      };
      if (!res) return;
      if (!res?.data || !res.data?.pages ) return
      if (res.data.pages.length == 0 ) return 
      if (res?.data && !res.data?.pages[res.data.pages.length - 1]?.nextPage) {
        await this.fetchExplorePage();
      }
    } else if (exploreQuery.hasNextPage || get(this.explorePosts).length === 0) {
      await this.fetchExplorePage();
    }
  }

  reset(): void {
    this.forYouPage = 1;
    this.explorePage = 1;

    this.forYouPosts.set([]);
    this.explorePosts.set([]);

    get(this.forYouQuery).remove();
    get(this.exploreQuery).remove();
  }
}
